import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import apiClient from '../../utils/axios';

const FETCH_TIMEOUT_MS = 15000;

const IntegrationTypesList = forwardRef(({ onCreateIntegration }, ref) => {
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [existingTypeIds, setExistingTypeIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  useImperativeHandle(ref, () => ({
    refresh: () => setFetchKey(prev => prev + 1)
  }));

  useEffect(() => {
    const controller = new AbortController();
    let isTimeout = false;

    const timeoutId = setTimeout(() => {
      isTimeout = true;
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    const fetchTypes = async () => {
      setLoading(true);
      setError(null);

      try {
        const [typesRes, integrationsRes] = await Promise.all([
          apiClient.get('/integrations/types', { signal: controller.signal }),
          apiClient.get('/integrations/', { signal: controller.signal })
        ]);

        clearTimeout(timeoutId);
        setIntegrationTypes(typesRes.data.integration_types || []);
        const existing = (integrationsRes.data.integrations || []).map(i => i.type_id);
        setExistingTypeIds(new Set(existing));
      } catch (err) {
        clearTimeout(timeoutId);
        if (isTimeout) {
          setError('Request timed out. Please try again later.');
        } else if (!controller.signal.aborted) {
          setError('Failed to load integration types');
        }
      } finally {
        if (!controller.signal.aborted || isTimeout) {
          setLoading(false);
        }
      }
    };

    fetchTypes();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchKey]);

  const handleRetry = useCallback(() => {
    setFetchKey(prev => prev + 1);
  }, []);

  // Loading placeholder
  if (loading) {
    return (
      <div className="dashboard-card">
        <h2>Available Integration Types</h2>
        <div className="types-list">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="type-item type-item-placeholder">
              <div className="type-details">
                <div className="placeholder-text placeholder-title"></div>
                <div className="placeholder-text placeholder-description"></div>
              </div>
              <div className="placeholder-button"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-card">
        <h2>Available Integration Types</h2>
        <div className="list-error">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{error}</div>
          <button className="retry-btn" onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card">
      <h2>Available Integration Types</h2>
      <div className="types-list">
        {integrationTypes.length === 0 ? (
          <div className="no-data">No integration types available</div>
        ) : (
          integrationTypes.map(type => {
            const alreadyAdded = existingTypeIds.has(type.type_id);
            return (
              <div key={type.type_id} className={`type-item${alreadyAdded ? ' type-item-disabled' : ''}`}>
                <div className="type-details">
                  <strong>{type.name}</strong>
                  <p className="type-description">{type.description}</p>
                </div>
                <div className="type-config">
                  {type.config_schema && (
                    <span className="config-fields">
                      {Object.keys(type.config_schema.properties || {}).length} fields
                    </span>
                  )}
                </div>
                <div className="type-actions">
                  <button 
                    className="add-integration-btn"
                    onClick={() => onCreateIntegration(type)}
                    title={alreadyAdded ? `${type.name} already added` : `Add ${type.name} integration`}
                    disabled={alreadyAdded}
                  >
                    {alreadyAdded ? '✓' : '+'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

IntegrationTypesList.displayName = 'IntegrationTypesList';

export default IntegrationTypesList;
